import type { PedidoReportOrder, PedidoReportSupplier } from "./types";

export type PedidoReportOrderState = "pronto" | "gerado" | "aguardando";
export type PedidoReportCheckboxState = "none" | "some" | "all";

export const pedidoReportKey = (supplierId: string, orderId: string) =>
  `${supplierId}:${orderId}`;

export function getPedidoReportOrderState(
  order: PedidoReportOrder,
): PedidoReportOrderState {
  if (order.readiness === "waiting") return "aguardando";
  return order.generated_at ? "gerado" : "pronto";
}

export function createInitialPedidoReportSelection(
  suppliers: PedidoReportSupplier[],
): Set<string> {
  return new Set(
    suppliers.flatMap((supplier) =>
      supplier.orders
        .filter((order) => order.readiness === "ready" && !order.generated_at)
        .map((order) => pedidoReportKey(supplier.supplier_id, order.order_id)),
    ),
  );
}

export function prunePedidoReportSelection(
  suppliers: PedidoReportSupplier[],
  selection: ReadonlySet<string>,
): Set<string> {
  const ready = new Set(
    suppliers.flatMap((supplier) =>
      supplier.orders
        .filter((order) => order.readiness === "ready")
        .map((order) => pedidoReportKey(supplier.supplier_id, order.order_id)),
    ),
  );
  return new Set([...selection].filter((key) => ready.has(key)));
}

export function reconcilePedidoReportExportSelection(
  previous: PedidoReportSupplier[],
  refreshed: PedidoReportSupplier[],
  selection: ReadonlySet<string>,
  preserve: ReadonlySet<string> = new Set(),
): Set<string> {
  const generatedAt = new Map(
    previous.flatMap((supplier) =>
      supplier.orders.map(
        (order) =>
          [
            pedidoReportKey(supplier.supplier_id, order.order_id),
            order.generated_at,
          ] as const,
      ),
    ),
  );
  const next = prunePedidoReportSelection(refreshed, selection);
  for (const supplier of refreshed) {
    for (const order of supplier.orders) {
      const key = pedidoReportKey(supplier.supplier_id, order.order_id);
      if (next.has(key) && order.generated_at !== generatedAt.get(key))
        next.delete(key);
    }
  }
  for (const key of prunePedidoReportSelection(refreshed, preserve))
    next.add(key);
  return next;
}

export function setPedidoReportOrderSelected(
  selection: ReadonlySet<string>,
  supplierId: string,
  order: PedidoReportOrder,
  selected: boolean,
): Set<string> {
  const next = new Set(selection);
  const key = pedidoReportKey(supplierId, order.order_id);
  if (selected && order.readiness === "ready") next.add(key);
  else next.delete(key);
  return next;
}

export function setPedidoReportOrdersSelected(
  selection: ReadonlySet<string>,
  suppliers: readonly PedidoReportSupplier[],
  selected: boolean,
): Set<string> {
  const next = new Set(selection);
  for (const supplier of suppliers) {
    for (const order of supplier.orders) {
      const key = pedidoReportKey(supplier.supplier_id, order.order_id);
      if (selected && order.readiness === "ready") next.add(key);
      else next.delete(key);
    }
  }
  return next;
}

export function getPedidoReportCheckboxState(
  selection: ReadonlySet<string>,
  supplier: PedidoReportSupplier,
): PedidoReportCheckboxState {
  const keys = supplier.orders
    .filter((order) => order.readiness === "ready")
    .map((order) => pedidoReportKey(supplier.supplier_id, order.order_id));
  const selected = keys.filter((key) => selection.has(key)).length;
  if (selected === 0) return "none";
  return selected === keys.length ? "all" : "some";
}

export function filterPedidoReportSuppliers(
  suppliers: PedidoReportSupplier[],
  search: string,
): PedidoReportSupplier[] {
  const normalizedSearch = normalizeSearch(search);
  const terms = normalizedSearch.split(/\s+/u).filter(Boolean);
  if (!terms.length) return suppliers;
  return suppliers.flatMap((supplier) => {
    const supplierName = normalizeSearch(supplier.supplier_name);
    if (terms.every((term) => supplierName.includes(term))) {
      return [supplier];
    }
    const orders = supplier.orders.filter((order) => {
      const names = normalizeSearch(
        [order.client_name, order.store_name].join(" "),
      );
      const number = normalizeSearch(order.order_number);
      return (
        terms.every((term) => names.includes(term)) ||
        terms.every((term) => term.length >= 2 && number.includes(term))
      );
    });
    return orders.length ? [{ ...supplier, orders }] : [];
  });
}

export function pagePedidoReportOrders(
  suppliers: PedidoReportSupplier[],
  page: number,
  pageSize: number,
): PedidoReportSupplier[] {
  let skip = Math.max(0, page) * Math.max(1, pageSize);
  let remaining = Math.max(1, pageSize);
  return suppliers.flatMap((supplier) => {
    if (!remaining) return [];
    const orders = supplier.orders.slice(skip, skip + remaining);
    skip = Math.max(0, skip - supplier.orders.length);
    remaining -= orders.length;
    return orders.length ? [{ ...supplier, orders }] : [];
  });
}

function normalizeSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLocaleLowerCase("pt-BR");
}
